import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin as MapPinIcon, X as CloseIcon } from "lucide-react";
import { Chalet } from "../types";

type Marker = {
  id: string;
  x: number; // 0..100 (%)
  y: number; // 0..100 (%)
};

function normalizeProvinceKey(p: string) {
  const s = (p ?? "").toLowerCase().trim();

  // Presets like: "Gelderland", "Friesland", "Noord-Holland", etc.
  if (s.includes("noord") && s.includes("holland")) return "noord_holland";
  if (s.includes("zuid") && s.includes("holland")) return "zuid_holland";
  if (s.includes("brabant")) return "noord_brabant";

  return s
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")
    .replace(/[^a-z_]/g, "")
    .replace(/_+/g, "_")
    .replace(/^_|_$/g, "");
}

// ✅ Echte pin marker (SVG)
const ProvincePin = ({ color }: { color: string }) => (
  <svg className="w-5 h-8 sm:w-8 sm:h-12" viewBox="0 0 24 36" aria-hidden="true">
    <path
      d="M12 0C6.9 0 2.8 4.1 2.8 9.2c0 7 9.2 16.6 9.2 16.6s9.2-9.6 9.2-16.6C21.2 4.1 17.1 0 12 0z"
      fill={color}
    />
    <circle cx="12" cy="9" r="3.5" fill="#fff" />
  </svg>
);

const PROVINCE_COLORS: Record<string, string> = {
  groningen: "#2563eb",
  friesland: "#0891b2",
  drenthe: "#16a34a",
  overijssel: "#a3760d",
  flevoland: "#f59e0b",
  gelderland: "#ea580c",
  utrecht: "#dc2626",
  noord_holland: "#9333ea",
  zuid_holland: "#6b21a8",
  zeeland: "#0f766e",
  noord_brabant: "#15803d",
  limburg: "#7c2d12",
};

// ✅ Your nl.svg uses SimpleMaps-style IDs (NLGR / NLFR / NLDR / ...)
const PROVINCE_SVG_ID: Record<string, string> = {
  groningen: "NLGR",
  friesland: "NLFR",
  drenthe: "NLDR",
  overijssel: "NLOV",
  flevoland: "NLFL",
  gelderland: "NLGE",
  utrecht: "NLUT",
  noord_holland: "NLNH",
  zuid_holland: "NLZH",
  zeeland: "NLZE",
  noord_brabant: "NLNB",
  limburg: "NLLI",
};

// Provincie “centers” fallback in percentage-coördinaten (used only if measurement fails)
const AMELAND_XY = { x: 59.85, y: 17 };										 
const PROVINCE_XY: Record<string, { x: number; y: number }> = {
  groningen: { x: 72, y: 18 },
  friesland: { x: 52, y: 20 },
  drenthe: { x: 66, y: 28 },
  overijssel: { x: 70, y: 42 },
  flevoland: { x: 56, y: 36 },
  gelderland: { x: 62, y: 52 },
  utrecht: { x: 52, y: 50 },
  noord_holland: { x: 40, y: 34 },
  zuid_holland: { x: 40, y: 50 },
  zeeland: { x: 30, y: 66 },
  noord_brabant: { x: 50, y: 72 },
  limburg: { x: 62, y: 86 },
};

// How far markers may spread *within each province* (ellipse radii in % of the container)
const PROVINCE_SPREAD: Record<string, { rx: number; ry: number }> = {
  groningen: { rx: 6.0, ry: 4.8 },
  friesland: { rx: 7.2, ry: 5.5 },
  drenthe: { rx: 5.8, ry: 4.6 },
  overijssel: { rx: 6.2, ry: 5.2 },
  flevoland: { rx: 4.2, ry: 3.4 },
  gelderland: { rx: 7.5, ry: 6.0 },
  utrecht: { rx: 3.6, ry: 3.2 },
  noord_holland: { rx: 6.2, ry: 6.0 },
  zuid_holland: { rx: 6.0, ry: 5.2 },
  zeeland: { rx: 6.0, ry: 4.6 },
  noord_brabant: { rx: 7.0, ry: 5.8 },
  limburg: { rx: 5.0, ry: 6.8 },
};

// Fine-tune province centers (in % of the map container)
// dx: +right / -left, dy: +down / -up
const PROVINCE_OFFSET: Record<string, { dx: number; dy: number }> = {
  // Small nudge so Drenthe sits a bit more centered
  drenthe: { dx: 0.6, dy: 0.8 },

  // Limburg needs to go south more (bigger positive dy)
  limburg: { dx: 0.0, dy: 4.0 },
};
function clampPct(v: number) {
  return Math.max(0, Math.min(100, v));
}

// deterministische jitter (zodat pins niet verspringen)
function hashToUnit(seed: string) {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}

function jitterXY(seed: string, radiusPct: number) {
  const u = hashToUnit(seed + ":u");
  const v = hashToUnit(seed + ":v");
  const r = Math.sqrt(u) * radiusPct;
  const t = v * Math.PI * 2;
  return { x: r * Math.cos(t), y: r * Math.sin(t) };
}

// Spread points inside an ellipse around the province center (golden-angle spiral)
function spreadInEllipse(
  base: { x: number; y: number },
  index: number,
  total: number,
  rx: number,
  ry: number
) {
  const ga = 2.399963229728653; // golden angle
  const t = index * ga;
  const r = total <= 1 ? 0 : Math.sqrt(index / (total - 1)); // 0..1
  return {
    x: base.x + rx * r * Math.cos(t),
    y: base.y + ry * r * Math.sin(t),
  };
}

const FALLBACK_IMG =
  "https://images.unsplash.com/photo-1542718610-a1d656d1884c?q=80&w=1200&auto=format&fit=crop";

export function MarkerMap({
  chalets,
  mapSrc = "svg/nl.svg", // change to svg/nl.svg
  jitterRadiusPct = 3.5,
}: {
  chalets: Chalet[];
  mapSrc?: string;
  jitterRadiusPct?: number;
}) {
  const navigate = useNavigate();

  // --- Popup state that supports smooth open/close/switch ---
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [displayedId, setDisplayedId] = useState<string | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);

  const displayed = displayedId ? chalets.find((c) => c.id === displayedId) : null;
  const displayedImg = displayed?.images?.[0] || FALLBACK_IMG;
  const isPopupOpen = Boolean(displayed);

  // ✅ Province center measurement (hidden SVG; does NOT change your UI)
  const [inlineSvg, setInlineSvg] = useState<string>("");
  const hiddenSvgWrapRef = useRef<HTMLDivElement | null>(null);
  const [svgCenters, setSvgCenters] = useState<Record<string, { x: number; y: number }>>({});

  useEffect(() => {
    let cancelled = false;
    fetch(mapSrc)
      .then((res) => res.text())
      .then((raw) => {
        if (cancelled) return;

        // Ensure the svg scales like your <img object-contain>
        const withSizing = raw.replace(
          /<svg\b([^>]*)>/i,
          `<svg $1 width="100%" height="100%" preserveAspectRatio="xMidYMid meet">`
        );

        setInlineSvg(withSizing);
      })
      .catch(() => setInlineSvg(""));

    return () => {
      cancelled = true;
    };
  }, [mapSrc]);

  useEffect(() => {
    if (!inlineSvg) return;
    const wrap = hiddenSvgWrapRef.current;
    if (!wrap) return;

    
const compute = () => {
  const svg = wrap.querySelector("svg") as SVGSVGElement | null;
  if (!svg) return;

  const wrapRect = wrap.getBoundingClientRect();
  if (wrapRect.width === 0 || wrapRect.height === 0) return;

  // Read SVG viewBox (so we can replicate <img className="object-contain"> math)
  const vb = svg.viewBox?.baseVal;
  const vbW = vb && vb.width ? vb.width : 0;
  const vbH = vb && vb.height ? vb.height : 0;
  if (!vbW || !vbH) return;

  // object-contain scaling: fit viewBox into container
  const scale = Math.min(wrapRect.width / vbW, wrapRect.height / vbH);
  const renderW = vbW * scale;
  const renderH = vbH * scale;
  const offsetX = (wrapRect.width - renderW) / 2;
  const offsetY = (wrapRect.height - renderH) / 2;

  const provinceKeys = Object.keys(PROVINCE_COLORS);
  const next: Record<string, { x: number; y: number }> = {};

  provinceKeys.forEach((key) => {
    const svgId = PROVINCE_SVG_ID[key] ?? key;

    // Primary: by ID (NLDR / NLFR / ...)
    let el = svg.querySelector<SVGGraphicsElement>(`#${CSS.escape(svgId)}`);

    // Fallback: by name="Drenthe" etc. (your SVG also contains name="...")
    if (!el) {
      const byName = key
        .split("_")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join("-"); // Noord-Holland, Zuid-Holland, Noord-Brabant
      el = svg.querySelector<SVGGraphicsElement>(`[name="${byName}"]`);
    }

    if (!el) return;

    // Center in SVG coordinate space (viewBox units)
    // getBBox() is in the SVG's internal coordinate system (stable, not affected by CSS)
    const b = el.getBBox();
    const cx = b.x + b.width / 2;
    const cy = b.y + b.height / 2;

    // Map SVG coords -> container coords using the same math as object-contain
    const px = offsetX + cx * scale;
    const py = offsetY + cy * scale;

    next[key] = {
      x: (px / wrapRect.width) * 100,
      y: (py / wrapRect.height) * 100,
    };
  });

  if (Object.keys(next).length > 0) setSvgCenters(next);
};

const raf = window.requestAnimationFrame(compute);
    const onResize = () => compute();
    window.addEventListener("resize", onResize);

    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, [inlineSvg]);

  // --- Marker coordinates (spread inside each province) ---
  const markers: Marker[] = useMemo(() => {
    const groups = new Map<string, Chalet[]>();
    for (const c of chalets) {
      const k = normalizeProvinceKey(c.province);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k)!.push(c);
    }

    // stable ordering so positions don’t jump
    for (const arr of groups.values()) {
      arr.sort((a, b) => a.id.localeCompare(b.id));
    }

    const out: Marker[] = [];
for (const [key, arr] of groups.entries()) {
  if (!(key in PROVINCE_SVG_ID)) continue;   // 👈 new: skips Germany

  // ✅ Use measured center if available (correct province mapping), otherwise fallback.
  const base0 = svgCenters[key] ?? PROVINCE_XY[key] ?? { x: 52, y: 50 };
	const off = PROVINCE_OFFSET[key] ?? { dx: 0, dy: 0 };
	const base = {
	  x: base0.x + off.dx,
	  y: base0.y + off.dy,
};																			
      const spread = PROVINCE_SPREAD[key] ?? { rx: 6.0, ry: 5.0 };

arr.forEach((c, idx) => {
  const location = (c.location ?? "").toLowerCase();

  // ✅ Special rule: Friesland + "Ameland" in the name => place on Ameland
  // If you have multiple Ameland chalets, they will overlap; you can optionally add a tiny offset.
  if (key === "friesland" && location.includes("ameland")) {
    out.push({
      id: c.id,
      x: AMELAND_XY.x,
      y: AMELAND_XY.y,
    });
    return;
  }

  const p = spreadInEllipse(base, idx, arr.length, spread.rx, spread.ry);
  const jit = jitterXY(c.id, Math.min(1.8, jitterRadiusPct * 0.45));

  out.push({
    id: c.id,
    x: clampPct(p.x + jit.x),
    y: clampPct(p.y + jit.y),
  });
});
    }
    return out;
  }, [chalets, jitterRadiusPct, svgCenters]);

  // --- Smooth transitions: open, close, switch ---
  const POPUP_MS = 180;

  useEffect(() => {
    // OPEN
    if (selectedId && !displayedId) {
      setDisplayedId(selectedId);
      const t = window.setTimeout(() => setPopupVisible(true), 0);
      return () => window.clearTimeout(t);
    }

    // SWITCH
    if (selectedId && displayedId && selectedId !== displayedId) {
      setPopupVisible(false);
      const t = window.setTimeout(() => {
        setDisplayedId(selectedId);
        window.setTimeout(() => setPopupVisible(true), 0);
      }, POPUP_MS);
      return () => window.clearTimeout(t);
    }

    // CLOSE
    if (!selectedId && displayedId) {
      setPopupVisible(false);
      const t = window.setTimeout(() => {
        setDisplayedId(null);
      }, POPUP_MS);
      return () => window.clearTimeout(t);
    }
  }, [selectedId, displayedId]);

  function closePopup(e?: React.MouseEvent) {
    if (e) e.stopPropagation();
    setSelectedId(null);
  }

  return (
    <div className="w-full">
      {/* click on background closes popup */}
      <div
        className="relative w-full max-w-full mx-auto aspect-[1/1.2] min-w-0"
        onClick={() => setSelectedId(null)}
      >
        {/* NL kaart (UNCHANGED UI) */}
        <img
          src={mapSrc}
          alt="Nederland provincies"
          className="absolute inset-0 w-full h-full object-contain drop-shadow-2xl select-none pointer-events-none"
          draggable={false}
        />

        {/* Hidden inline svg for measurement only (does NOT affect layout/visuals) */}
        <div
          ref={hiddenSvgWrapRef}
          className="absolute inset-0 w-full h-full opacity-0 pointer-events-none"
          aria-hidden="true"
          dangerouslySetInnerHTML={{ __html: inlineSvg }}
        />

        {/* markers */}
        {markers.map((m) => {
          const chalet = chalets.find((c) => c.id === m.id);
          const provinceKey = normalizeProvinceKey(chalet?.province ?? "");
          const color = PROVINCE_COLORS[provinceKey] ?? "#111827";
          const active = selectedId === m.id;

          return (
            <button
              key={m.id}
              type="button"
              onClick={(e) => {
                e.stopPropagation(); // prevent background click close
                setSelectedId((prev) => (prev === m.id ? null : m.id));
              }}
              className="absolute z-20 w-[56px] h-[56px] sm:w-[72px] sm:h-[72px] -translate-x-1/2 -translate-y-full"
              style={{ left: `${m.x}%`, top: `${m.y}%` }}
              aria-label="Marker"
              aria-pressed={active}
            >
              <span className="absolute inset-0 rounded-full bg-transparent" />

              <span className="absolute left-1/2 bottom-0 -translate-x-1/2">
                <span className="block scale-[0.6] sm:scale-100 origin-bottom">
                  <ProvincePin color={color} />
                </span>
              </span>
            </button>
          );
        })}

        {/* Desktop popup */}
        <div
          className={[
            "hidden lg:block absolute left-1/4 top-1/4 -translate-y-1/2 -translate-x-[70%] w-[280px] z-30",
            "transition-all duration-200 ease-out",
            isPopupOpen
              ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
              : "opacity-0 translate-y-1 scale-[0.98] pointer-events-none",
          ].join(" ")}
          onClick={(e) => e.stopPropagation()}
        >
          {displayed && (
            <div
              className={[
                "relative bg-white rounded-3xl shadow-2xl border border-brand-50 p-3",
                "transition-opacity duration-200",
                popupVisible ? "opacity-100" : "opacity-0",
              ].join(" ")}
            >
              <div className="relative z-0 h-28 rounded-2xl overflow-hidden">
                <img src={displayedImg} alt={displayed.title} className="w-full h-full object-cover relative z-0" />
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-brand-900 text-white text-[10px] font-black uppercase rounded-full">
                  €{displayed.pricePerNight}
                </div>
              </div>

              <button
                type="button"
                onClick={closePopup}
                aria-label="Sluiten"
                className="absolute right-2 top-2 z-30 w-8 h-8 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center pointer-events-auto"
              >
                <CloseIcon size={16} className="text-stone-700" />
              </button>

              <div className="mt-3">
                <div className="text-[12px] font-black text-brand-900 uppercase tracking-tight truncate">
                  {displayed.title}
                </div>
                <div className="mt-1 text-[11px] text-stone-500 font-bold flex items-center gap-1">
                  <MapPinIcon size={12} />
                  {displayed.location}, {displayed.province}
                </div>

                <button
                  type="button"
                  className="w-full mt-3 py-2 bg-brand-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all"
                  onClick={() => navigate(`/chalet/${displayed.id}`)}
                >
                  Bekijk verblijf
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile popup */}
        <div
          className={[
            "lg:hidden absolute left-1/2 -translate-x-1/2 top-4 w-[min(92vw,320px)] z-30",
            "transition-all duration-200 ease-out",
            isPopupOpen
              ? "opacity-100 translate-y-0 scale-100 pointer-events-auto"
              : "opacity-0 -translate-y-2 scale-[0.98] pointer-events-none",
          ].join(" ")}
          onClick={(e) => e.stopPropagation()}
        >
          {displayed && (
            <div
              className={[
                "relative bg-white rounded-3xl shadow-2xl border border-brand-50 p-3",
                "transition-opacity duration-200",
                popupVisible ? "opacity-100" : "opacity-0",
              ].join(" ")}
            >
              <div className="relative z-0 h-28 rounded-2xl overflow-hidden">
                <img src={displayedImg} alt={displayed.title} className="w-full h-full object-cover relative z-0" />
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-brand-900 text-white text-[10px] font-black uppercase rounded-full">
                  €{displayed.pricePerNight}
                </div>
              </div>

              <button
                type="button"
                onClick={closePopup}
                aria-label="Sluiten"
                className="absolute right-2 top-2 z-30 w-8 h-8 rounded-full bg-white/95 hover:bg-white shadow flex items-center justify-center pointer-events-auto"
              >
                <CloseIcon size={16} className="text-stone-700" />
              </button>

              <div className="mt-3">
                <div className="text-[12px] font-black text-brand-900 uppercase tracking-tight truncate">
                  {displayed.title}
                </div>
                <div className="mt-1 text-[11px] text-stone-500 font-bold flex items-center gap-1">
                  <MapPinIcon size={12} />
                  {displayed.location}, {displayed.province}
                </div>

                <button
                  type="button"
                  className="w-full mt-3 py-2 bg-brand-900 text-white text-[10px] font-black uppercase tracking-widest rounded-xl hover:bg-black transition-all"
                  onClick={() => navigate(`/chalet/${displayed.id}`)}
                >
                  Bekijk verblijf
                </button>
              </div>
            </div>
          )}
        </div>

        {chalets.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center text-stone-400 text-sm font-bold">
            Geen huisjes om te tonen
          </div>
        )}
      </div>
    </div>
  );
}
